import * as THREE from "three";
import { LAYER_OUT, type Garment } from "./clothing";
import { buildGunMesh, type WeaponDef } from "./weapon";

/**
 * The hybrid character: Roblox-R15 joint structure built at Minecraft box
 * proportions, textured from a 64x64 Minecraft skin, with a SKINNED warp rig.
 *
 * Each body part (torso, each arm, each leg) is a single SkinnedMesh on a small
 * bone chain. WARP joints (spine, elbow, wrist, knee, ankle) are bones INSIDE a
 * part, so bending blends the shared cross-section — no gap, soft seam. FLOAT
 * joints (neck, shoulder, hip) are rigid bone parenting BETWEEN parts, so the
 * separate meshes may drift a hair apart (the 3 exceptions). The head is a rigid
 * box parented to the neck bone.
 *
 * All geometry is authored in MODEL space (rest pose, feet at y=0); the shared
 * skeleton's bind pose matches. `root.position` plants the feet; faces +Z.
 */

const TEX = 64;

const D = {
  headW: 8, headH: 8, headD: 8,
  torsoW: 8, torsoD: 4, uTorsoH: 6, lTorsoH: 6,
  armW: 4, armD: 4, uArmH: 6, lArmH: 4, handH: 2,
  legW: 4, legD: 4, uLegH: 5, lLegH: 5, footH: 2,
};

// Scale so the MIDDLE of the head lines up with the zombies first-person eye
// height (WORLD.playerHeight = 1.7). head-center px = legs + torso + headH/2.
const EYE_HEIGHT = 1.7;
const HEAD_CENTER_PX = D.footH + D.lLegH + D.uLegH + D.lTorsoH + D.uTorsoH + D.headH / 2;
const PX = EYE_HEIGHT / HEAD_CENTER_PX;
const U = (px: number) => px * PX;
const WARP_BAND = U(1.25); // ± skin-px around a warp joint that blends two bones
const ROUND_SUBDIV = 8; // columns/grid per box face when roundness is on (enough to curve)
type Dims = typeof D;

// ---- body customization ------------------------------------------------------
/** Per-character body shape. Multipliers are 1 = default; shaping is 0 = none.
 *  Players may set ANY value (even ones that break the mesh — their funeral). */
export interface BodyConfig {
  height: number; width: number; depth: number;         // global Y / X / Z scale
  limbLength: number; torsoLength: number; headSize: number; // per-region length
  bust: number; hips: number; waist: number;            // Type B shaping (0 = none)
  rear: number;                                          // posterior protrusion (twin-shaped, like the bust)
  thighs: number;                                        // fatten the upper leg (tapers to the knee)
  // per-region roundness (0 = sharp box … 1 = fully rounded)
  roundHead: number; roundTorso: number; roundArmU: number; roundArmL: number; roundLegU: number; roundLegL: number;
  wobble: number;   // velocity-driven whole-body secondary motion (gait only, was "jiggle")
  jiggle: number;   // per-bone soft-body jiggle vs. velocity (gait only)
}
const ALL_ROUND = { roundHead: 0.3, roundTorso: 0.3, roundArmU: 0.3, roundArmL: 0.3, roundLegU: 0.3, roundLegL: 0.3 };
// body types: TYPE A = the neutral base; TYPE B = bust/rear/hips/waist shaping.
export const DEFAULT_BODY: BodyConfig = { height: 1, width: 1, depth: 1, limbLength: 1, torsoLength: 1, headSize: 1, bust: 0, hips: 0, waist: 0, rear: 0, thighs: 0, ...ALL_ROUND, wobble: 0.5, jiggle: 0.5 };
export const TYPE_B_BODY: BodyConfig = { height: 0.97, width: 0.94, depth: 0.95, limbLength: 1, torsoLength: 1, headSize: 1, bust: 0.6, hips: 0.6, waist: 0.5, rear: 0.55, thighs: 0.35, roundHead: 0.35, roundTorso: 0.35, roundArmU: 0.35, roundArmL: 0.35, roundLegU: 0.35, roundLegL: 0.35, wobble: 0.7, jiggle: 0.8 };

/** Base px dims scaled by the config's global (H/W/D) + per-region multipliers.
 *  Every dim is floored to a tiny positive value: players may make the body as
 *  absurd as they like, but a zero/negative dimension would divide-by-zero into
 *  NaN geometry (breaking the renderer, not just the character), so we clamp. */
function effectiveDims(c: BodyConfig): Dims {
  const H = c.height, W = c.width, De = c.depth, L = c.limbLength, T = c.torsoLength, Hs = c.headSize;
  const f = (v: number) => (v > 0.02 ? v : 0.02);
  return {
    headW: f(D.headW * W * Hs), headH: f(D.headH * H * Hs), headD: f(D.headD * De * Hs),
    torsoW: f(D.torsoW * W), torsoD: f(D.torsoD * De), uTorsoH: f(D.uTorsoH * H * T), lTorsoH: f(D.lTorsoH * H * T),
    armW: f(D.armW * W), armD: f(D.armD * De), uArmH: f(D.uArmH * H * L), lArmH: f(D.lArmH * H * L), handH: f(D.handH * H * L),
    legW: f(D.legW * W), legD: f(D.legD * De), uLegH: f(D.uLegH * H * L), lLegH: f(D.lLegH * H * L), footH: f(D.footH * H * L),
  };
}

interface YLines { ground: number; ankle: number; knee: number; hip: number; spine: number; shoulder: number; elbow: number; wrist: number; }
/** Model-space Y of the key joint lines, from the effective dims. */
function computeY(d: Dims): YLines {
  const hip = U(d.footH + d.lLegH + d.uLegH);
  const shoulder = U(d.footH + d.lLegH + d.uLegH + d.lTorsoH + d.uTorsoH);
  return {
    ground: 0, ankle: U(d.footH), knee: U(d.footH + d.lLegH), hip,
    spine: U(d.footH + d.lLegH + d.uLegH + d.lTorsoH), shoulder,
    elbow: shoulder - U(d.uArmH), wrist: shoulder - U(d.uArmH) - U(d.lArmH),
  };
}

/** Model-space rig metrics the gait/IK needs. hipX is slightly wider than legW/2
 *  so the ball-jointed hips can show a thigh gap. */
export interface Rig { thigh: number; calf: number; footH: number; footD: number; hipY: number; hipX: number; shoulderY: number; }
function computeRig(d: Dims, Y: YLines): Rig {
  return { thigh: U(d.uLegH), calf: U(d.lLegH), footH: U(d.footH), footD: U(d.legD), hipY: Y.hip, hipX: U(d.legW / 2 + 0.4), shoulderY: Y.shoulder };
}

/** Model-space vertex warp for the torso profile: protrude the front (bust) and the
 *  lower back (posterior) — each as TWO distinct shapes via a twin lateral profile —
 *  pinch the waist at the upper/lower seam, widen the hips at the bottom. */
type Deform = (v: Vec3) => Vec3;
const IDENTITY_DEFORM: Deform = (v) => v;
function makeTorsoDeform(c: BodyConfig, yBot: number, yTop: number, ySpine: number, hzTorso: number, hxTorso: number): Deform {
  if (c.bust === 0 && c.hips === 0 && c.waist === 0 && c.rear === 0) return IDENTITY_DEFORM;
  const span = yTop - yBot, tWaist = (ySpine - yBot) / span, tBust = tWaist + (1 - tWaist) * 0.5;
  const tRear = tWaist * 0.3; // the seat: peaks just above the torso bottom
  return (v) => {
    const t = clamp((v[1] - yBot) / span, 0, 1);
    const waistH = c.waist * hump(t, tWaist, 0.34);                    // peak at the seam
    const hipH = c.hips * smooth(clamp((0.42 - t) / 0.42, 0, 1));      // max at the bottom
    // twin lateral profile: peaks at ±half the half-width with a soft central valley,
    // tapering to the sides — the bust reads as two distinct shapes, and so does the
    // posterior. The 0.3 floor keeps a shallow join instead of a knife-edge cleft.
    const lx = Math.abs(v[0]) / hxTorso;                               // 0 center → 1 side edge
    const twin = 0.3 + 0.7 * smooth(clamp(1 - Math.abs(lx - 0.5) / 0.45, 0, 1));
    const bustH = c.bust * hump(t, tBust, 0.26) * twin;                // peak upper chest
    const rearH = c.rear * hump(t, tRear, 0.3) * twin;                 // peak at the seat
    const xf = 1 - 0.34 * waistH + 0.42 * hipH;                        // pinch waist / widen hips (X)
    const zf = 1 - 0.18 * waistH + 0.32 * hipH;                        // slight waist / rear (Z)
    const front = clamp(v[2] / hzTorso, 0, 1);                         // 0 at center/back → 1 at the front
    const back = clamp(-v[2] / hzTorso, 0, 1);                         // 0 at center/front → 1 at the back
    const nz = v[2] * zf + front * hzTorso * 0.95 * bustH - back * hzTorso * 0.9 * rearH;
    return [v[0] * xf, v[1], nz];
  };
}
/** Socket weld: SWELL a limb's top slightly and push it INTO the torso, so the two
 *  rounded surfaces overlap instead of abutting — the rounding on each box otherwise
 *  pulls both surfaces off the shared plane and opens a visible groove at the seam.
 *  Buried inside the torso, the junction reads as one continuous surface with a
 *  natural muscle crease where the limb emerges (deltoid / hip). The short anchored
 *  weight band above is still the only part that's truly "welded" — the connection
 *  is a socket, not the whole limb end. `inward` shifts toward the body's centerline;
 *  `up` buries the cap upward (legs meet the torso's BOTTOM face, so they need it). */
function makeWeldDeform(cx: number, yTopWeld: number, weldH: number, inward: number, up = 0): Deform {
  return (v) => {
    const t = smooth(clamp((v[1] - (yTopWeld - weldH * 2)) / (weldH * 2), 0, 1)); // 0 below the weld → 1 at the top
    const s = 1 + 0.16 * t; // gentle flare — closes the rounding groove by interpenetration
    return [(v[0] - cx) * s + cx + inward * t, v[1] + up * t, v[2] * s];
  };
}
/** Fatten the upper leg (X+Z) about the leg's center `cx`, full at the hip and
 *  smoothly tapering back to 1.0 by the knee (so the lower leg is untouched). */
function makeThighDeform(c: BodyConfig, yKnee: number, yHip: number, cx: number): Deform {
  if (c.thighs === 0 || yHip <= yKnee) return IDENTITY_DEFORM;
  return (v) => {
    const s = 1 + c.thighs * smooth(clamp((v[1] - yKnee) / (yHip - yKnee), 0, 1));
    return [(v[0] - cx) * s + cx, v[1], v[2] * s];
  };
}
/** Round the sharp box corners/edges: a rounded-box SDF projection that pulls
 *  edge/corner vertices inward and leaves face interiors flat. `rTop`/`rBot` are
 *  the roundness (0..1) at the top/bottom of the box, interpolated by height so a
 *  part can round its upper region differently from its lower (per-joint). */
function makeRoundDeform(xc: number, zc: number, yBot: number, yTop: number, hx: number, hz: number, rTop: number, rBot: number): Deform {
  if (rTop <= 0 && rBot <= 0) return IDENTITY_DEFORM;
  const yc = (yTop + yBot) / 2, hy = (yTop - yBot) / 2, span = yTop - yBot;
  const rMul = Math.min(hx, hy, hz) * 0.6; // roundness=1 → radius = 0.6·smallest half-extent
  return (v) => {
    const t = clamp((v[1] - yBot) / span, 0, 1); // 0 bottom, 1 top
    const r = (rBot + (rTop - rBot) * t) * rMul;
    if (r < 1e-5) return v;
    const px = v[0] - xc, py = v[1] - yc, pz = v[2] - zc;
    const qx = clamp(px, -(hx - r), hx - r), qy = clamp(py, -(hy - r), hy - r), qz = clamp(pz, -(hz - r), hz - r);
    const dx = px - qx, dy = py - qy, dz = pz - qz;
    const len = Math.hypot(dx, dy, dz);
    if (len < 1e-6) return v; // interior — unchanged
    const s = r / len;
    return [xc + qx + dx * s, yc + qy + dy * s, zc + qz + dz * s];
  };
}
/** Compose two deforms (apply `a` then `b`). */
function composeDeform(a: Deform, b: Deform): Deform {
  if (a === IDENTITY_DEFORM) return b;
  if (b === IDENTITY_DEFORM) return a;
  return (v) => b(a(v));
}
function hump(t: number, ctr: number, w: number): number { const dd = Math.abs(t - ctr) / w; return dd >= 1 ? 0 : smooth(1 - dd); }
function clamp(x: number, lo: number, hi: number): number { return x < lo ? lo : x > hi ? hi : x; }
function smooth(t: number): number { return t * t * (3 - 2 * t); }

// --- Minecraft 64x64 atlas (Rect = px0,py0,px1,py1; py from top) ------------
export type Rect = readonly [number, number, number, number];
export interface FaceRects { front: Rect; back: Rect; right: Rect; left: Rect; top: Rect; bottom: Rect; }
interface PartSkin { base: FaceRects; overlay: FaceRects; }

export const SKIN = {
  head: {
    base: { top: [8, 0, 16, 8], bottom: [16, 0, 24, 8], right: [0, 8, 8, 16], front: [8, 8, 16, 16], left: [16, 8, 24, 16], back: [24, 8, 32, 16] },
    overlay: { top: [40, 0, 48, 8], bottom: [48, 0, 56, 8], right: [32, 8, 40, 16], front: [40, 8, 48, 16], left: [48, 8, 56, 16], back: [56, 8, 64, 16] },
  },
  body: {
    base: { top: [20, 16, 28, 20], bottom: [28, 16, 36, 20], right: [16, 20, 20, 32], front: [20, 20, 28, 32], left: [28, 20, 32, 32], back: [32, 20, 40, 32] },
    overlay: { top: [20, 32, 28, 36], bottom: [28, 32, 36, 36], right: [16, 36, 20, 48], front: [20, 36, 28, 48], left: [28, 36, 32, 48], back: [32, 36, 40, 48] },
  },
  rightArm: {
    base: { top: [44, 16, 48, 20], bottom: [48, 16, 52, 20], right: [40, 20, 44, 32], front: [44, 20, 48, 32], left: [48, 20, 52, 32], back: [52, 20, 56, 32] },
    overlay: { top: [44, 32, 48, 36], bottom: [48, 32, 52, 36], right: [40, 36, 44, 48], front: [44, 36, 48, 48], left: [48, 36, 52, 48], back: [52, 36, 56, 48] },
  },
  leftArm: {
    base: { top: [36, 48, 40, 52], bottom: [40, 48, 44, 52], right: [32, 52, 36, 64], front: [36, 52, 40, 64], left: [40, 52, 44, 64], back: [44, 52, 48, 64] },
    overlay: { top: [52, 48, 56, 52], bottom: [56, 48, 60, 52], right: [48, 52, 52, 64], front: [52, 52, 56, 64], left: [56, 52, 60, 64], back: [60, 52, 64, 64] },
  },
  rightLeg: {
    base: { top: [4, 16, 8, 20], bottom: [8, 16, 12, 20], right: [0, 20, 4, 32], front: [4, 20, 8, 32], left: [8, 20, 12, 32], back: [12, 20, 16, 32] },
    overlay: { top: [4, 32, 8, 36], bottom: [8, 32, 12, 36], right: [0, 36, 4, 48], front: [4, 36, 8, 48], left: [8, 36, 12, 48], back: [12, 36, 16, 48] },
  },
  leftLeg: {
    base: { top: [20, 48, 24, 52], bottom: [24, 48, 28, 52], right: [16, 52, 20, 64], front: [20, 52, 24, 64], left: [24, 52, 28, 64], back: [28, 52, 32, 64] },
    overlay: { top: [4, 48, 8, 52], bottom: [8, 48, 12, 52], right: [0, 52, 4, 64], front: [4, 52, 8, 64], left: [8, 52, 12, 64], back: [12, 52, 16, 64] },
  },
} as const satisfies Record<string, PartSkin>;

const OVERLAY_INFLATE = U(0.75);
// Voxelized overlay ("real 3-D layer"): each opaque overlay pixel becomes an
// actual cube protruding ONE (scaled) texel out of the base surface — so hair,
// hats and clothing get a true 3-D silhouette and cast shadows, and grow/shrink
// with the character. Protrusion depth is computed per-face in partFaces().
const VOXEL_ALPHA = 128; // an overlay pixel is "solid" at alpha >= this (0..255)

type Vec3 = [number, number, number];
type WeightList = Array<{ i: number; w: number }>;
type SkinPixels = { data: Uint8ClampedArray; w: number; h: number };
/** One box whose overlay layer gets voxelized, in MODEL space. */
export type BodyRegion = "torso" | "arm" | "leg" | "head";
interface VoxelDef {
  faces: FaceRects; xc: number; zc: number; wPx: number; dPx: number;
  yTop: number; yBot: number; weightFor: (y: number) => WeightList; deform: Deform;
  baseFaces: FaceRects; // the part's BASE-layer rects — garments painted there count too
  region: BodyRegion; sx: number; // which body region + its side sign (for garment shaping)
}
/** Model-space info a body-aware garment shape needs (bust lift, etc.). */
export interface GarmentDims {
  region: BodyRegion; sx: number; yHip: number; ySpine: number; yShoulder: number;
  hxTorso: number; hzTorso: number; bust: number; hips: number; waist: number; rear: number;
}
const IDENTITY_MAT = new THREE.Matrix4(); // bind matrix: geometry is authored in model space
const _magL = new THREE.Vector3(), _supL = new THREE.Vector3(); // reload-anim scratch
/** smoothstep of x over [a,b] */
function ss(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

/** Exposed joints (bones) for posing + IK. */
export interface CharacterJoints {
  pelvis: THREE.Bone; spine: THREE.Bone; neck: THREE.Bone;
  shoulderL: THREE.Bone; elbowL: THREE.Bone; wristL: THREE.Bone;
  shoulderR: THREE.Bone; elbowR: THREE.Bone; wristR: THREE.Bone;
  hipL: THREE.Bone; kneeL: THREE.Bone; ankleL: THREE.Bone;
  hipR: THREE.Bone; kneeR: THREE.Bone; ankleR: THREE.Bone;
}

/** 1-axis hinge limits (radians) for the warp joints, applied during IK (M6).
 *  Elbows/knees fold one way only; spine bends gently. */
export const JOINT_LIMITS = {
  elbow: { axis: "x", min: -2.4, max: 0.0 }, // forearm folds forward only
  knee: { axis: "x", min: 0.0, max: 2.4 }, // calf folds backward only
  wrist: { axis: "x", min: -0.6, max: 0.6 },
  ankle: { axis: "x", min: -0.7, max: 0.7 },
  spine: { axis: "x", min: -0.5, max: 0.8 },
} as const;

export class Character {
  readonly root = new THREE.Group();
  joints: CharacterJoints;
  rig!: Rig;
  private cfg: BodyConfig;
  private d!: Dims;
  private Y!: YLines;
  private readonly baseMat: THREE.MeshLambertMaterial;
  private readonly overlayMat: THREE.MeshLambertMaterial;
  private readonly bones: THREE.Bone[] = [];
  private readonly index = new Map<THREE.Bone, number>();

  // Overlay/"3-D layer" rendering: "voxel" = real protruding cubes (default),
  // "flat" = the old inflated-PNG shell, "off" = base only. Toggle live to A/B.
  overlayMode: "voxel" | "flat" | "off" = "voxel";
  private readonly voxelDefs: VoxelDef[] = [];
  // layered clothing: one garment per SLOT (body role — a bra + panties + shirt + pants
  // can coexist), each its own voxel-shell SkinnedMesh. The garment's LAYER only sets how
  // far the shell protrudes (underwear < inner < outer < hat), so slots at deeper layers
  // sit over shallower ones.
  private readonly wardrobe = new Map<string, Garment>();
  private readonly garmentMeshes = new Map<string, THREE.SkinnedMesh>();
  private readonly garmentMats = new Map<string, THREE.MeshLambertMaterial>();
  private readonly flatOverlay: THREE.Mesh[] = [];
  private voxelMesh: THREE.SkinnedMesh | null = null;
  private headMeshRef: THREE.Mesh | null = null;
  private skeleton!: THREE.Skeleton;
  private pixels: SkinPixels;
  // held weapon: a holder on the SPINE bone (so the barrel points chest-forward =
  // the aim). null = unequipped. Re-attached after a setBody rebuild.
  private weaponHolder: THREE.Object3D | null = null;
  private curWeapon: WeaponDef | null = null;
  private readonly foreLocal = new THREE.Vector3();
  private readonly muzzleLocal = new THREE.Vector3();
  private readonly holderBase = new THREE.Vector3();
  private magMesh: THREE.Mesh | null = null;
  private reloadEase = 0;
  weaponDom: "right" | "left" = "right";
  // combat → animation blackboard (written by Combat, read here + by the gait)
  gunKick = 0;   // 0..1 recoil (kick² drives the shove)
  reloadP = -1;  // reload progress 0..1, or -1 when not reloading
  swing = 0;     // 1→0 melee swing envelope
  holdT = 0;     // grenade cook-hold amount
  throwT = 0;    // 1→0 grenade throw envelope

  constructor(skin: THREE.Texture, cfg: BodyConfig = DEFAULT_BODY) {
    this.baseMat = new THREE.MeshLambertMaterial({ map: skin });
    this.overlayMat = new THREE.MeshLambertMaterial({ map: skin, transparent: true, alphaTest: VOXEL_ALPHA / 255 });
    this.pixels = readPixels(skin);
    this.cfg = { ...cfg };
    this.recompute();
    this.joints = this.build();
  }

  get body(): BodyConfig { return { ...this.cfg }; }
  get wobble(): number { return this.cfg.wobble; } // whole-body secondary motion (gait, live)
  get jiggle(): number { return this.cfg.jiggle; } // per-bone soft-body jiggle (gait, live)
  get height(): number { return this.Y.shoulder + U(this.d.headH); }
  get eyeHeight(): number { return this.Y.shoulder + U(this.d.headH * 0.6); }
  setGroundPosition(x: number, y: number, z: number): void { this.root.position.set(x, y, z); }

  private recompute(): void {
    this.d = effectiveDims(this.cfg);
    this.Y = computeY(this.d);
    this.rig = computeRig(this.d, this.Y);
  }

  /** Apply a new body configuration and rebuild the geometry + skeleton from
   *  scratch. The caller MUST retune the gait afterward (proportions changed). */
  setBody(cfg: BodyConfig): void {
    // wobble + jiggle are animation-only (read live by the gait) — if nothing
    // else changed, skip the geometry/skeleton rebuild so their sliders stay smooth.
    const geomChanged = (Object.keys(cfg) as (keyof BodyConfig)[]).some((k) => k !== "wobble" && k !== "jiggle" && cfg[k] !== this.cfg[k]);
    this.cfg = { ...cfg };
    if (!geomChanged) return;
    this.recompute();
    this.root.traverse((o) => { const g = (o as Partial<THREE.Mesh>).geometry; if (g) g.dispose(); });
    this.root.clear();
    this.skeleton?.dispose();
    this.bones.length = 0; this.index.clear();
    this.voxelDefs.length = 0; this.flatOverlay.length = 0;
    this.voxelMesh = null;
    this.weaponHolder = null; // its spine parent was just cleared
    this.joints = this.build();
    if (this.curWeapon) this.setWeapon(this.curWeapon, this.weaponDom); // re-attach on the new spine
  }

  /** Swap the Minecraft skin texture on every part (base + overlay). The
   *  voxel layer is rebuilt because it depends on the new skin's alpha mask. */
  setSkin(tex: THREE.Texture): void {
    const old = this.baseMat.map;
    this.baseMat.map = tex;
    this.overlayMat.map = tex;
    this.baseMat.needsUpdate = true;
    this.overlayMat.needsUpdate = true;
    this.pixels = readPixels(tex);
    this.buildVoxels();
    if (old && old !== tex) old.dispose();
  }

  /** The current skin texture's source image (for the skin editor to seed from). */
  get skinImage(): CanvasImageSource | null { return (this.baseMat.map?.image as CanvasImageSource) ?? null; }

  /** Re-read the (edited-in-place) skin pixels and rebuild the voxel overlay so
   *  painted overlay pixels create/remove 3-D voxels. Base edits show live already. */
  repaint(): void {
    if (this.baseMat.map) { this.pixels = readPixels(this.baseMat.map); this.baseMat.map.needsUpdate = true; }
    this.buildVoxels();
  }

  /** Fade the whole character (base + overlay + the FaceAnim-swapped head material)
   *  to opacity `a` — used when the collision camera is forced very close so it
   *  doesn't stare through the skull. overlayMat stays transparent for its alphaTest
   *  voxel cull, so hair/hats fade WITH the body. */
  setViewOpacity(a: number): void {
    if (a === this._viewOpacity) return; // no per-frame churn at the a===1 steady state
    this._viewOpacity = a;
    const t = a < 0.999;
    this.overlayMat.opacity = a; // overlayMat.transparent already true (alphaTest cull) — no version bump
    const hm = this.headMeshRef?.material as THREE.MeshLambertMaterial | undefined;
    for (const m of hm && hm !== this.baseMat ? [this.baseMat, hm] : [this.baseMat]) {
      m.opacity = a;
      if (m.transparent !== t) { m.transparent = t; m.needsUpdate = true; } // version bump only when the flag flips
    }
  }
  private _viewOpacity = 1;

  /** Current overlay render mode (so the skin editor can restore it on exit). */
  get overlay(): "voxel" | "flat" | "off" { return this.overlayMode; }
  /** A copy of the live body configuration (for avatar export). */
  get bodyConfig(): BodyConfig { return { ...this.cfg }; }

  /** The head mesh — FaceAnim swaps a shader-patched material onto it (and only it). */
  get headMesh(): THREE.Mesh | null { return this.headMeshRef; }
  /** The shared base material (FaceAnim clones it for the head + restores it on pause). */
  get baseMaterial(): THREE.MeshLambertMaterial { return this.baseMat; }

  /** Switch how the overlay/"3-D layer" renders: voxel cubes, flat shell, off. */
  setOverlay(mode: "voxel" | "flat" | "off"): void {
    this.overlayMode = mode;
    if (this.voxelMesh) this.voxelMesh.visible = mode === "voxel";
    for (const m of this.flatOverlay) m.visible = mode === "flat";
  }

  /** Equip (or unequip, def=null) a two-handed gun held in the `dominant` hand. */
  setWeapon(def: WeaponDef | null, dominant: "right" | "left"): void {
    if (this.weaponHolder) {
      this.weaponHolder.parent?.remove(this.weaponHolder);
      this.weaponHolder.traverse((o) => {
        const g = (o as Partial<THREE.Mesh>).geometry; g?.dispose?.();
        const m = (o as Partial<THREE.Mesh>).material as THREE.Material | undefined; m?.dispose?.();
      });
      this.weaponHolder = null;
      this.magMesh = null;
    }
    this.curWeapon = def;
    this.weaponDom = dominant;
    this.reloadEase = 0; // a mid-reload unequip must not tilt the NEXT gun
    this.reloadP = -1;
    this.gunKick = 0;
    this.swing = 0;
    if (!def) return;
    const { mesh, foreGrip, muzzle } = buildGunMesh(def);
    // the skin editor's raycasts must ignore the gun (its UV boxes would otherwise
    // become paint targets and corrupt the 64×64 skin atlas)
    mesh.traverse((o) => { o.userData.noPaint = true; });
    const holder = new THREE.Object3D();
    holder.add(mesh);
    // in front of the chest on the dominant side, barrel (+Z) along chest-forward
    const dx = dominant === "right" ? -U(2.4) : U(2.4);
    holder.position.set(dx, (this.Y.shoulder - this.Y.spine) * 0.5, U(4.5));
    this.holderBase.copy(holder.position);
    this.joints.spine.add(holder);
    this.weaponHolder = holder;
    this.foreLocal.copy(foreGrip);
    this.muzzleLocal.copy(muzzle);
    this.magMesh = mesh.getObjectByName("mag") as THREE.Mesh | null;
  }

  /** World grip points for the arm IK: dominant hand → the gun grip (holder
   *  origin), support hand → the fore-grip. During a reload the SUPPORT hand
   *  runs a mag-swap path (down to the magazine, pull out+down, push back, return).
   *  false if unequipped. */
  gripPoints(dominantOut: THREE.Vector3, supportOut: THREE.Vector3): boolean {
    if (!this.weaponHolder) return false;
    this.weaponHolder.getWorldPosition(dominantOut);
    const p = this.reloadEase;
    if (p > 0.02 && this.magMesh) {
      // phase the support hand through the reload: foreGrip → mag → out/down → mag → foreGrip
      const rp = this.reloadP >= 0 ? this.reloadP : 1;
      _magL.copy(this.magMesh.position);
      if (rp < 0.25) { const u = ss(0, 0.25, rp); _supL.lerpVectors(this.foreLocal, _magL, u); }
      else if (rp < 0.5) { _supL.copy(_magL); _supL.y -= 0.15 * ss(0.25, 0.5, rp); }
      else if (rp < 0.8) { _supL.copy(_magL); _supL.y -= 0.15 * (1 - ss(0.5, 0.8, rp)); }
      else { const u = ss(0.8, 1, rp); _supL.lerpVectors(_magL, this.foreLocal, u); }
      this.magMesh.visible = !(rp >= 0.25 && rp < 0.55); // the mag is "out" mid-swap
      supportOut.lerpVectors(this.foreLocal, _supL, Math.min(1, p * 2));
    } else {
      if (this.magMesh) this.magMesh.visible = true;
      supportOut.copy(this.foreLocal);
    }
    this.weaponHolder.localToWorld(supportOut);
    return true;
  }

  /** World-space muzzle tip (tracer/flash origin). false if unequipped. */
  muzzleWorld(out: THREE.Vector3): boolean {
    if (!this.weaponHolder) return false;
    out.copy(this.muzzleLocal);
    this.weaponHolder.localToWorld(out);
    return true;
  }

  /** Whether the current weapon wants both hands on it (melee is one-handed). */
  get twoHanded(): boolean { return !!this.curWeapon && !this.curWeapon.melee; }
  get weaponDef(): WeaponDef | null { return this.curWeapon; }

  /** Per-frame combat pose on the gun HOLDER (recoil shove + reload tilt + melee
   *  swing arc). Called by the gait before the arm IK, so the hands — IK'd onto
   *  the grip points — ride every motion (recoil drags the arms, the swing carries
   *  the hand through the arc). Holder frame: +Z chest-forward, +Y up. */
  applyGunPose(dt = 1 / 60): void {
    if (!this.weaponHolder) return;
    // ease the reload amount so completion doesn't snap the hands (dt-based)
    const rTgt = this.reloadP >= 0 ? 1 : 0;
    this.reloadEase += (rTgt - this.reloadEase) * Math.min(1, 8.5 * dt);
    const kick = this.gunKick * this.gunKick;
    let px = this.holderBase.x, py = this.holderBase.y, pz = this.holderBase.z - kick * 0.06;
    let rx = -kick * 0.25 + 0.4 * this.reloadEase;
    // cooking/throwing a grenade → drop the weapon to a one-handed low carry so the
    // support hand plausibly holds it while the dominant hand works the grenade
    const carry = Math.max(this.holdT, this.throwT);
    if (carry > 0.01) { py -= 0.1 * carry; pz -= 0.08 * carry; rx += 0.5 * carry; }
    if (this.swing > 0.005 && this.curWeapon?.melee) {
      // wind up over the shoulder, then sweep down-forward through the strike
      const p = 1 - this.swing;
      const up = 1 - ss(0.22, 0.42, p);
      const strike = ss(0.3, 0.62, p) * (1 - ss(0.78, 1, p));
      py += 0.22 * up - 0.12 * strike;
      pz += -0.12 * up + 0.42 * strike;
      rx += 1.0 * up - 1.5 * strike;
    } else if (this.swing > 0.005) {
      rx += -0.5 * Math.sin((1 - this.swing) * Math.PI); // armed F-jab: a quick muzzle shove
      pz += 0.1 * Math.sin((1 - this.swing) * Math.PI);
    }
    this.weaponHolder.position.set(px, py, pz);
    this.weaponHolder.rotation.set(rx, 0, 0);
  }

  /** (Re)build the single voxel SkinnedMesh from the current skin's alpha mask.
   *  Bound with an identity bind-matrix (geometry is model-space) so it behaves
   *  identically whether built at spawn or rebuilt after the root has moved. */
  private buildVoxels(): void {
    if (this.voxelMesh) {
      this.root.remove(this.voxelMesh);
      this.voxelMesh.geometry.dispose();
      this.voxelMesh = null;
    }
    const px = this.pixels;
    const solid = (c: number, r: number): boolean =>
      c >= 0 && r >= 0 && c < px.w && r < px.h && px.data[(r * px.w + c) * 4 + 3] >= VOXEL_ALPHA;

    const b = new GeomBuilder();
    for (const def of this.voxelDefs) emitPartVoxels(b, def, solid);
    const geo = b.finish();
    if (geo.getIndex()!.count === 0) { geo.dispose(); return; } // no overlay pixels

    const mesh = new THREE.SkinnedMesh(geo, this.baseMat);
    mesh.name = "voxel-overlay"; // skin editor tags hits on this as the "overlay" atlas layer
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.frustumCulled = false;
    mesh.visible = this.overlayMode === "voxel";
    this.root.add(mesh);
    mesh.bind(this.skeleton, IDENTITY_MAT);
    this.voxelMesh = mesh;
  }

  // ---- layered clothing -----------------------------------------------------

  /** Equip a garment (replaces whatever occupies its SLOT). */
  equipClothing(g: Garment): void {
    const slot = g.slot ?? g.layer;
    this.wardrobe.set(slot, g);
    const oldMat = this.garmentMats.get(slot);
    if (oldMat) { oldMat.dispose(); this.garmentMats.delete(slot); }
    this.buildGarment(slot);
  }

  /** Remove the garment in a slot (back to whatever is underneath). */
  removeClothing(slot: string): void {
    this.wardrobe.delete(slot);
    this.disposeGarment(slot, true);
  }

  /** The currently equipped garments (innermost first is not guaranteed). */
  get clothing(): Garment[] { return [...this.wardrobe.values()]; }

  private disposeGarment(slot: string, dropMat = false): void {
    const m = this.garmentMeshes.get(slot);
    if (m) { this.root.remove(m); m.geometry.dispose(); this.garmentMeshes.delete(slot); }
    if (dropMat) { this.garmentMats.get(slot)?.dispose(); this.garmentMats.delete(slot); }
  }

  /** (Re)build one slot's voxel shell from its garment texture. Cloth may be
   *  painted in a part's base OR overlay atlas region — both passes are emitted. */
  private buildGarment(slot: string): void {
    this.disposeGarment(slot);
    const g = this.wardrobe.get(slot);
    if (!g || !this.skeleton) return;
    const px = readPixels(g.tex);
    const solidAt = (c: number, r: number): boolean =>
      c >= 0 && r >= 0 && c < px.w && r < px.h && px.data[(r * px.w + c) * 4 + 3] >= VOXEL_ALPHA;
    const out = LAYER_OUT[g.layer];
    const b = new GeomBuilder();
    const dimsBase = {
      yHip: this.Y.hip, ySpine: this.Y.spine, yShoulder: this.Y.shoulder,
      hxTorso: U(this.d.torsoW) / 2, hzTorso: U(this.d.torsoD) / 2,
      bust: this.cfg.bust, hips: this.cfg.hips, waist: this.cfg.waist, rear: this.cfg.rear,
    };
    for (const def of this.voxelDefs) {
      // body-aware shaping (bra lift, etc.) composes AFTER the region's body deform
      const extra = g.shape?.({ ...dimsBase, region: def.region, sx: def.sx }) ?? null;
      const dd = extra ? { ...def, deform: composeDeform(def.deform, extra as Deform) } : def;
      emitPartVoxels(b, dd, solidAt, out, g.extrude);
      emitPartVoxels(b, { ...dd, faces: dd.baseFaces }, solidAt, out, g.extrude);
    }
    const geo = b.finish();
    if (geo.getIndex()!.count === 0) { geo.dispose(); return; }
    let mat = this.garmentMats.get(slot);
    if (!mat) {
      mat = new THREE.MeshLambertMaterial({ map: g.tex, transparent: true, alphaTest: VOXEL_ALPHA / 255 });
      this.garmentMats.set(slot, mat);
    }
    const mesh = new THREE.SkinnedMesh(geo, mat);
    mesh.name = "garment-" + slot;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.frustumCulled = false;
    this.root.add(mesh);
    mesh.bind(this.skeleton, IDENTITY_MAT);
    this.garmentMeshes.set(slot, mesh);
  }

  /** Rebuild every equipped garment (after a body rebuild replaced the skeleton). */
  private rebuildClothing(): void {
    this.garmentMeshes.clear(); // the old meshes died with the body rebuild's teardown
    for (const slot of this.wardrobe.keys()) this.buildGarment(slot);
  }

  private bone(name: keyof CharacterJoints, parent: THREE.Object3D, x: number, y: number, z: number): THREE.Bone {
    const b = new THREE.Bone();
    b.name = name;
    b.position.set(x, y, z);
    parent.add(b);
    this.index.set(b, this.bones.length);
    this.bones.push(b);
    return b;
  }

  private build(): CharacterJoints {
    const d = this.d, Y = this.Y;
    // Bind in MODEL space: park the root at the origin so the skeleton's bone-
    // inverses and EVERY mesh's bind-matrix are root-independent (identity).
    // Otherwise a setBody() rebuild while the character is out in the world binds
    // the base meshes at the world transform but the voxel mesh at identity, so
    // they drift apart the instant the body poses. Restored at the very end.
    const savedPos = this.root.position.clone();
    const savedQuat = this.root.quaternion.clone();
    this.root.position.set(0, 0, 0);
    this.root.quaternion.identity();

    // --- skeleton (local positions relative to parent bone) ---
    const pelvis = this.bone("pelvis", this.root, 0, Y.hip, 0);
    const spine = this.bone("spine", pelvis, 0, Y.spine - Y.hip, 0);
    const neck = this.bone("neck", spine, 0, Y.shoulder - Y.spine, 0);

    const armX = U(d.torsoW / 2 + d.armW / 2);
    const shoulderR = this.bone("shoulderR", spine, -armX, Y.shoulder - Y.spine, 0);
    const elbowR = this.bone("elbowR", shoulderR, 0, Y.elbow - Y.shoulder, 0);
    const wristR = this.bone("wristR", elbowR, 0, Y.wrist - Y.elbow, 0);
    const shoulderL = this.bone("shoulderL", spine, armX, Y.shoulder - Y.spine, 0);
    const elbowL = this.bone("elbowL", shoulderL, 0, Y.elbow - Y.shoulder, 0);
    const wristL = this.bone("wristL", elbowL, 0, Y.wrist - Y.elbow, 0);

    const legX = this.rig.hipX;
    const hipR = this.bone("hipR", pelvis, -legX, 0, 0);
    const kneeR = this.bone("kneeR", hipR, 0, Y.knee - Y.hip, 0);
    const ankleR = this.bone("ankleR", kneeR, 0, Y.ankle - Y.knee, 0);
    const hipL = this.bone("hipL", pelvis, legX, 0, 0);
    const kneeL = this.bone("kneeL", hipL, 0, Y.knee - Y.hip, 0);
    const ankleL = this.bone("ankleL", kneeL, 0, Y.ankle - Y.knee, 0);

    this.root.updateMatrixWorld(true);
    const skeleton = new THREE.Skeleton(this.bones);

    // --- skinned parts ---
    // roundness bevels every part's box corners; part-specific shapes (torso
    // bust/waist/hips, leg thighs) compose ON TOP of the rounded box. Rounding
    // needs the box SUBDIVIDED (else pulling the 4 corners in just shrinks the
    // rectangle) — `sub` columns/grid only when roundness is on.
    // Per-region roundness (interpolated top→bottom within a part so upper/lower
    // arm & leg round independently). Only subdivide a part if IT is rounded.
    const c = this.cfg;
    const subFor = (a: number, b: number) => (a > 0 || b > 0 ? ROUND_SUBDIV : 1);
    const torsoShape = makeTorsoDeform(this.cfg, Y.hip, Y.shoulder, Y.spine, U(d.torsoD) / 2, U(d.torsoW) / 2);
    const torso = composeDeform(makeRoundDeform(0, 0, Y.hip, Y.shoulder, U(d.torsoW) / 2, U(d.torsoD) / 2, c.roundTorso, c.roundTorso), torsoShape);
    this.part(SKIN.body, 0, 0, d.torsoW, d.torsoD, Y.shoulder, Y.hip, [
      { yTop: Y.shoulder, yBot: Y.spine, bone: spine },
      { yTop: Y.spine, yBot: Y.hip, bone: pelvis },
    ], skeleton, torso, subFor(Math.max(c.roundTorso, c.bust, c.rear), c.roundTorso)); // twin bust/rear shapes need columns even unrounded

    // --- limb WELDS: the top of each arm/leg is anchored to the TORSO's bone (spine /
    // pelvis) with the standard warp blend beneath, so posing a limb stretches it
    // smoothly out of the torso instead of separating at a float joint. The weld cap
    // is deliberately SHORT (a socket, not half the limb) and the weld deform narrows
    // + tucks it into the torso — the connecting part is smaller than the full limb
    // cross-section, like a real shoulder. Texture rows inside the weld compress.
    const armBot = Y.wrist - U(d.handH);
    const armWeldH = U(d.uArmH) * 0.2; // top ~20% of the upper arm = the shoulder cap
    const arm = (part: PartSkin, sx: number, sh: THREE.Bone, el: THREE.Bone, wr: THREE.Bone) =>
      this.part(part, sx, 0, d.armW, d.armD, Y.shoulder, armBot, [
        { yTop: Y.shoulder, yBot: Y.shoulder - armWeldH, bone: spine }, // welded to the torso
        { yTop: Y.shoulder - armWeldH, yBot: Y.elbow, bone: sh },
        { yTop: Y.elbow, yBot: Y.wrist, bone: el },
        { yTop: Y.wrist, yBot: armBot, bone: wr },
      ], skeleton, composeDeform(
        makeRoundDeform(sx, 0, armBot, Y.shoulder, U(d.armW) / 2, U(d.armD) / 2, c.roundArmU, c.roundArmL),
        makeWeldDeform(sx, Y.shoulder, armWeldH, -Math.sign(sx) * U(1.2), U(0.35)),
      ), subFor(c.roundArmU, c.roundArmL), "arm");
    arm(SKIN.rightArm, -armX, shoulderR, elbowR, wristR);
    arm(SKIN.leftArm, armX, shoulderL, elbowL, wristL);

    const legWeldH = U(d.uLegH) * 0.16; // hip socket cap
    const leg = (part: PartSkin, sx: number, hp: THREE.Bone, kn: THREE.Bone, an: THREE.Bone) =>
      this.part(part, sx, 0, d.legW, d.legD, Y.hip, Y.ground, [
        { yTop: Y.hip, yBot: Y.hip - legWeldH, bone: pelvis }, // welded to the pelvis
        { yTop: Y.hip - legWeldH, yBot: Y.knee, bone: hp },
        { yTop: Y.knee, yBot: Y.ankle, bone: kn },
        { yTop: Y.ankle, yBot: Y.ground, bone: an },
      ], skeleton, composeDeform(
        composeDeform(makeRoundDeform(sx, 0, Y.ground, Y.hip, U(d.legW) / 2, U(d.legD) / 2, c.roundLegU, c.roundLegL), makeThighDeform(this.cfg, Y.knee, Y.hip, sx)),
        makeWeldDeform(sx, Y.hip, legWeldH, -Math.sign(sx) * U(0.7), U(0.9)),
      ), subFor(c.roundLegU, c.roundLegL), "leg");
    leg(SKIN.rightLeg, -legX, hipR, kneeR, ankleR);
    leg(SKIN.leftLeg, legX, hipL, kneeL, ankleL);

    // --- head (rigid, parented to the neck bone) ---
    // head box is authored LOCAL (centered at origin); its round deform is local.
    const headSub = subFor(c.roundHead, c.roundHead);
    const headRoundLocal = makeRoundDeform(0, 0, -U(d.headH) / 2, U(d.headH) / 2, U(d.headW) / 2, U(d.headD) / 2, c.roundHead, c.roundHead);
    const headGeo = boxGeometry(U(d.headW), U(d.headH), U(d.headD), allFaces(SKIN.head.base), headRoundLocal, headSub);
    const head = new THREE.Mesh(headGeo, this.baseMat);
    head.name = "head";
    this.headMeshRef = head; // FaceAnim swaps a shader-patched material onto just this mesh
    head.position.y = U(d.headH / 2); // local to neck bone (at Y.shoulder)
    head.castShadow = true;
    head.receiveShadow = true;
    neck.add(head);
    // flat "hat" shell — kept for the 'flat' toggle; hidden by default. Rounded
    // with the same local deform as the head base so 'flat' mode stays consistent.
    const hatGeo = boxGeometry(U(d.headW) + OVERLAY_INFLATE, U(d.headH) + OVERLAY_INFLATE, U(d.headD) + OVERLAY_INFLATE, allFaces(SKIN.head.overlay), headRoundLocal, 1);
    const hat = new THREE.Mesh(hatGeo, this.overlayMat);
    hat.position.y = U(d.headH / 2);
    hat.visible = false;
    neck.add(hat);
    this.flatOverlay.push(hat);

    // head voxels ride the neck bone rigidly. Authored in MODEL space: the head
    // box spans [Y.shoulder, Y.shoulder + headH], centered on x/z.
    const neckIdx = this.index.get(neck)!;
    this.voxelDefs.push({
      faces: SKIN.head.overlay, baseFaces: SKIN.head.base, xc: 0, zc: 0, wPx: d.headW, dPx: d.headD,
      yTop: Y.shoulder + U(d.headH), yBot: Y.shoulder, weightFor: () => [{ i: neckIdx, w: 1 }],
      deform: makeRoundDeform(0, 0, Y.shoulder, Y.shoulder + U(d.headH), U(d.headW) / 2, U(d.headD) / 2, c.roundHead, c.roundHead),
      region: "head", sx: 0,
    });

    this.skeleton = skeleton;
    this.buildVoxels();
    this.rebuildClothing(); // equipped garments follow the (re)built body + skeleton

    // restore the character's world placement (bind is now captured at identity)
    this.root.position.copy(savedPos);
    this.root.quaternion.copy(savedQuat);
    this.root.updateMatrixWorld(true);

    return { pelvis, spine, neck, shoulderL, elbowL, wristL, shoulderR, elbowR, wristR, hipL, kneeL, ankleL, hipR, kneeR, ankleR };
  }

  /** Build one body part (base + overlay SkinnedMeshes) bound to the skeleton. */
  private part(
    skin: PartSkin, xc: number, zc: number, wPx: number, dPx: number,
    yTop: number, yBot: number, segs: Array<{ yTop: number; yBot: number; bone: THREE.Bone }>,
    skeleton: THREE.Skeleton, deform: Deform = IDENTITY_DEFORM, sub = 1,
    region: BodyRegion = "torso",
  ): void {
    const idx = (b: THREE.Bone) => this.index.get(b)!;
    const bands = segs.map((s) => ({ yTop: s.yTop, yBot: s.yBot, i: idx(s.bone) }));
    const weightFor = makeWeightFor(bands, WARP_BAND);

    const base = skinnedPartGeometry(xc, zc, U(wPx), U(dPx), yTop, yBot, skin.base, weightFor, deform, sub);
    const baseMesh = new THREE.SkinnedMesh(base, this.baseMat);
    this.bindPart(baseMesh, skeleton, true);

    // flat inflated-PNG overlay — hidden debug toggle; keep it cheap (sub=1).
    const ov = skinnedPartGeometry(
      xc, zc, U(wPx) + OVERLAY_INFLATE, U(dPx) + OVERLAY_INFLATE,
      yTop + OVERLAY_INFLATE / 2, yBot - OVERLAY_INFLATE / 2, skin.overlay, weightFor, deform, 1,
    );
    const ovMesh = new THREE.SkinnedMesh(ov, this.overlayMat);
    ovMesh.visible = false;
    this.bindPart(ovMesh, skeleton, false);
    this.flatOverlay.push(ovMesh);

    // voxelized overlay: real protruding cubes, same warp weights + deform as base.
    this.voxelDefs.push({ faces: skin.overlay, baseFaces: skin.base, xc, zc, wPx, dPx, yTop, yBot, weightFor, deform, region, sx: xc });
  }

  private bindPart(mesh: THREE.SkinnedMesh, skeleton: THREE.Skeleton, castShadow: boolean): void {
    mesh.castShadow = castShadow;
    mesh.receiveShadow = true;
    mesh.frustumCulled = false; // bind-pose bounds are wrong once posed
    this.root.add(mesh);
    mesh.bind(skeleton);
  }
}

// --- weighting ---------------------------------------------------------------
type WeightFn = (y: number) => Array<{ i: number; w: number }>;

/** Blend the two bones around each internal joint over ±band; rigid elsewhere. */
function makeWeightFor(bands: Array<{ yTop: number; yBot: number; i: number }>, band: number): WeightFn {
  return (y: number) => {
    for (let k = 0; k < bands.length - 1; k++) {
      const boundary = bands[k].yBot; // == bands[k+1].yTop
      if (Math.abs(y - boundary) < band) {
        const t = (y - (boundary - band)) / (2 * band); // 1 at top(upper bone), 0 at bottom(lower)
        return [{ i: bands[k].i, w: t }, { i: bands[k + 1].i, w: 1 - t }];
      }
    }
    // not in a blend band: pick the segment that contains y (clamp to ends)
    for (const b of bands) if (y <= b.yTop + 1e-6 && y >= b.yBot - 1e-6) return [{ i: b.i, w: 1 }];
    return [{ i: (y > bands[0].yTop ? bands[0] : bands[bands.length - 1]).i, w: 1 }];
  };
}

// --- geometry ----------------------------------------------------------------
const SIDES: ReadonlyArray<{ key: keyof FaceRects; n: THREE.Vector3Tuple; r: THREE.Vector3Tuple }> = [
  { key: "front", n: [0, 0, 1], r: [1, 0, 0] },
  { key: "back", n: [0, 0, -1], r: [-1, 0, 0] },
  { key: "right", n: [-1, 0, 0], r: [0, 0, 1] }, // -X = character's right
  { key: "left", n: [1, 0, 0], r: [0, 0, -1] }, // +X = character's left
];

// UV inset (in texels): face rects used to run EXACTLY to their atlas-texel borders,
// so edge fragments' interpolated UVs could round across into the NEIGHBORING atlas
// texel — usually transparent (black RGB on an opaque material) → the hairline "pixel
// gaps" along edges and corners. Insetting keeps every sample strictly inside the face.
const UV_EPS = 0.03;

/** Geometric normal of a quad (A=TL,B=TR,C=BR,D=BL) from its diagonals, signed
 *  to the reference. On a flat quad = the face normal; on a bevelled/rounded
 *  quad it tilts so the deformed surface shades correctly. */
function quadNormal(A: Vec3, B: Vec3, C: Vec3, D: Vec3, ref: THREE.Vector3Tuple): Vec3 {
  const ax = C[0] - A[0], ay = C[1] - A[1], az = C[2] - A[2];
  const bx = D[0] - B[0], by = D[1] - B[1], bz = D[2] - B[2];
  let nx = ay * bz - az * by, ny = az * bx - ax * bz, nz = ax * by - ay * bx;
  const len = Math.hypot(nx, ny, nz) || 1; nx /= len; ny /= len; nz /= len;
  return (nx * ref[0] + ny * ref[1] + nz * ref[2] < 0) ? [-nx, -ny, -nz] : [nx, ny, nz];
}

/** A skinned body part: 4 side faces (row- AND column-subdivided so `deform`
 *  roundness forms a real curve, not just a smaller box) + top/bottom cap grids,
 *  authored in model space, with per-vertex skin weights. `sub` = columns/face. */
function skinnedPartGeometry(
  xc: number, zc: number, wU: number, dU: number, yTop: number, yBot: number,
  faces: FaceRects, weightFor: WeightFn, deform: Deform = IDENTITY_DEFORM, sub = 1,
): THREE.BufferGeometry {
  const hx = wU / 2, hz = dU / 2;
  const rows = Math.max(2, Math.round((yTop - yBot) / PX)); // ~1 skin-px per row
  const b = new GeomBuilder();

  for (const f of SIDES) {
    const rect = faces[f.key];
    const hr = f.r[0] !== 0 ? hx : hz;
    const pt = (s: number, y: number): Vec3 => deform([xc + f.n[0] * hx + f.r[0] * hr * s, y, zc + f.n[2] * hz + f.r[2] * hr * s]);
    const uOf = (s: number) => (rect[0] + UV_EPS + ((s + 1) / 2) * (rect[2] - rect[0] - 2 * UV_EPS)) / TEX; // interpolate across the column
    const vOf = (y: number) => 1 - (rect[1] + UV_EPS + ((yTop - y) / (yTop - yBot)) * (rect[3] - rect[1] - 2 * UV_EPS)) / TEX;
    for (let i = 0; i < rows; i++) {
      const ya = yTop - ((yTop - yBot) * i) / rows, yb = yTop - ((yTop - yBot) * (i + 1)) / rows;
      const wa = weightFor(ya), wb = weightFor(yb); // row-invariant across the sub-column loop
      for (let csub = 0; csub < sub; csub++) {
        const s0 = -1 + (2 * csub) / sub, s1 = -1 + (2 * (csub + 1)) / sub;
        const A = pt(s0, ya), B = pt(s1, ya), C = pt(s1, yb), D = pt(s0, yb);
        b.quad(A, B, C, D, quadNormal(A, B, C, D, f.n),
          [uOf(s0), vOf(ya)], [uOf(s1), vOf(ya)], [uOf(s1), vOf(yb)], [uOf(s0), vOf(yb)],
          wa, wa, wb, wb);
      }
    }
  }

  const wTop = weightFor(yTop), wBot = weightFor(yBot);
  cap(b, [0, 1, 0], [1, 0, 0], [0, 0, -1], xc, yTop, zc, hx, hz, faces.top, wTop, deform, sub);
  cap(b, [0, -1, 0], [1, 0, 0], [0, 0, 1], xc, yBot, zc, hx, hz, faces.bottom, wBot, deform, sub);
  return b.finish();
}

/** An end-cap (top/bottom) as an sub×sub grid so roundness curves its rim too. */
function cap(
  b: GeomBuilder, n: THREE.Vector3Tuple, r: THREE.Vector3Tuple, ud: THREE.Vector3Tuple,
  xc: number, y: number, zc: number, hx: number, hz: number, rect: Rect, w: Array<{ i: number; w: number }>,
  deform: Deform = IDENTITY_DEFORM, sub = 1,
): void {
  const hr = r[0] !== 0 ? hx : hz, hu = ud[0] !== 0 ? hx : hz;
  const corner = (su: number, sr: number): Vec3 => deform([
    xc + ud[0] * hu * su + r[0] * hr * sr, y + ud[1] * hu * su + r[1] * hr * sr, zc + ud[2] * hu * su + r[2] * hr * sr,
  ]);
  const u0 = (rect[0] + UV_EPS) / TEX, u1 = (rect[2] - UV_EPS) / TEX, v0 = 1 - (rect[1] + UV_EPS) / TEX, v1 = 1 - (rect[3] - UV_EPS) / TEX;
  const uOf = (sr: number) => u0 + ((sr + 1) / 2) * (u1 - u0);
  const vOf = (su: number) => v0 + ((1 - su) / 2) * (v1 - v0);
  for (let i = 0; i < sub; i++) {
    const su0 = 1 - (2 * i) / sub, su1 = 1 - (2 * (i + 1)) / sub;
    for (let jsub = 0; jsub < sub; jsub++) {
      const sr0 = -1 + (2 * jsub) / sub, sr1 = -1 + (2 * (jsub + 1)) / sub;
      const A = corner(su0, sr0), B = corner(su0, sr1), C = corner(su1, sr1), D = corner(su1, sr0);
      b.quad(A, B, C, D, quadNormal(A, B, C, D, n),
        [uOf(sr0), vOf(su0)], [uOf(sr1), vOf(su0)], [uOf(sr1), vOf(su1)], [uOf(sr0), vOf(su1)], w, w, w, w);
    }
  }
}

/** Accumulates a triangle-indexed BufferGeometry with skin attributes. */
class GeomBuilder {
  private pos: number[] = [];
  private nor: number[] = [];
  private uv: number[] = [];
  private si: number[] = [];
  private sw: number[] = [];
  private idx: number[] = [];
  private v = 0;

  quad(
    A: THREE.Vector3Tuple, B: THREE.Vector3Tuple, C: THREE.Vector3Tuple, D: THREE.Vector3Tuple,
    n: THREE.Vector3Tuple,
    uvA: [number, number], uvB: [number, number], uvC: [number, number], uvD: [number, number],
    wA: Array<{ i: number; w: number }>, wB: Array<{ i: number; w: number }>,
    wC: Array<{ i: number; w: number }>, wD: Array<{ i: number; w: number }>,
  ): void {
    this.vert(A, n, uvA, wA); this.vert(B, n, uvB, wB); this.vert(C, n, uvC, wC); this.vert(D, n, uvD, wD);
    const o = this.v;
    this.idx.push(o + 0, o + 3, o + 2, o + 0, o + 2, o + 1); // TL,BL,BR + TL,BR,TR (outward)
    this.v += 4;
  }

  private vert(p: THREE.Vector3Tuple, n: THREE.Vector3Tuple, uv: [number, number], w: Array<{ i: number; w: number }>): void {
    this.pos.push(p[0], p[1], p[2]);
    this.nor.push(n[0], n[1], n[2]);
    this.uv.push(uv[0], uv[1]);
    for (let k = 0; k < 4; k++) { this.si.push(w[k]?.i ?? 0); this.sw.push(w[k]?.w ?? 0); }
  }

  finish(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute("normal", new THREE.Float32BufferAttribute(this.nor, 3));
    g.setAttribute("uv", new THREE.Float32BufferAttribute(this.uv, 2));
    g.setAttribute("skinIndex", new THREE.Uint16BufferAttribute(this.si, 4));
    g.setAttribute("skinWeight", new THREE.Float32BufferAttribute(this.sw, 4));
    g.setIndex(this.idx);
    return g;
  }
}

// --- voxelized overlay ("real 3-D layer") ------------------------------------
/** Read a texture's RGBA pixels via an offscreen canvas (row 0 = top, matching
 *  the Minecraft atlas convention used by the Rect tables). */
function readPixels(tex: THREE.Texture): SkinPixels {
  const img = tex.image as CanvasImageSource & { width: number; height: number };
  const w = img.width, h = img.height;
  const cv = document.createElement("canvas");
  cv.width = w; cv.height = h;
  const ctx = cv.getContext("2d", { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0);
  return { data: ctx.getImageData(0, 0, w, h).data, w, h };
}

interface VFace { key: keyof FaceRects; rect: Rect; O: Vec3; du: Vec3; dv: Vec3; udir: Vec3; vdir: Vec3; n: Vec3; depth: number; }

/** The 6 faces of a box as texel frames, DERIVED from the base geometry's own
 *  UV mapping so a voxel for texel (col,row) lands exactly where the base layer
 *  paints it. Per-texel steps (du/dv) and the protrusion `depth` are the SCALED
 *  world-size of one skin-pixel on each axis (face-extent ÷ rect-texel-count), so
 *  the voxels grow/shrink with the character instead of staying at the default PX. */
function partFaces(def: VoxelDef): VFace[] {
  const { faces, xc, zc, wPx, dPx, yTop, yBot } = def;
  const hx = U(wPx) / 2, hz = U(dPx) / 2;
  // world size of one texel along each axis (front rect: u=X, v=Y; right rect: u=Z)
  const pxX = U(wPx) / (faces.front[2] - faces.front[0]);
  const pxY = (yTop - yBot) / (faces.front[3] - faces.front[1]);
  const pxZ = U(dPx) / (faces.right[2] - faces.right[0]);
  const sides: Array<{ key: keyof FaceRects; n: Vec3; r: Vec3 }> = [
    { key: "front", n: [0, 0, 1], r: [1, 0, 0] },
    { key: "back", n: [0, 0, -1], r: [-1, 0, 0] },
    { key: "right", n: [-1, 0, 0], r: [0, 0, 1] }, // -X = character's right
    { key: "left", n: [1, 0, 0], r: [0, 0, -1] }, // +X = character's left
  ];
  const out: VFace[] = [];
  for (const s of sides) {
    const hn = Math.abs(s.n[0]) * hx + Math.abs(s.n[2]) * hz;
    const hr = Math.abs(s.r[0]) * hx + Math.abs(s.r[2]) * hz;
    const pxU = s.r[0] !== 0 ? pxX : pxZ;  // u runs along X (front/back) or Z (left/right)
    const depth = s.n[0] !== 0 ? pxX : pxZ; // protrude one texel along the normal (X or Z)
    out.push({
      key: s.key,
      rect: faces[s.key],
      O: [xc + s.n[0] * hn - s.r[0] * hr, yTop, zc + s.n[2] * hn - s.r[2] * hr],
      du: [s.r[0] * pxU, 0, s.r[2] * pxU],
      dv: [0, -pxY, 0],
      udir: [s.r[0], 0, s.r[2]],
      vdir: [0, -1, 0],
      n: s.n, depth,
    });
  }
  out.push({ key: "top", rect: faces.top, O: [xc - hx, yTop, zc - hz], du: [pxX, 0, 0], dv: [0, 0, pxZ], udir: [1, 0, 0], vdir: [0, 0, 1], n: [0, 1, 0], depth: pxY });
  out.push({ key: "bottom", rect: faces.bottom, O: [xc - hx, yBot, zc + hz], du: [pxX, 0, 0], dv: [0, 0, -pxZ], udir: [1, 0, 0], vdir: [0, 0, -1], n: [0, -1, 0], depth: pxY });
  return out;
}

/** Emit one part's voxel shell: for each solid pixel, an outward cap plus a
 *  wall on any side whose in-plane neighbor is absent (so only silhouette edges
 *  get walls — interiors stay hollow). Each voxel is one solid texel color. The
 *  four SURFACE corners are run through the part's `deform` (so voxels follow a
 *  bust/waist/hip profile) and then extruded along the box normal.
 *  `out` scales the protrusion (in texel-depths): 1 = the classic skin overlay,
 *  clothing layers use fractions of it so a full stack tops out at 1. `extrude`
 *  adds per-texel height on top (hat brims/crowns) — a height CHANGE between
 *  neighbors also emits a wall, so stepped extrusions stay closed. */
function emitPartVoxels(
  b: GeomBuilder, def: VoxelDef, solid: (c: number, r: number) => boolean,
  out = 1, extrude?: (c: number, r: number) => number,
): void {
  const deform = def.deform;
  const hAt = (c: number, r: number) => out + (extrude ? extrude(c, r) : 0);
  const faces = partFaces(def);
  for (const f of faces) {
    const [px0, py0, px1, py1] = f.rect;
    const { O, du, dv, n, udir, vdir, depth } = f;
    for (let row = py0; row < py1; row++) {
      for (let col = px0; col < px1; col++) {
        if (!solid(col, row)) continue;
        const h = hAt(col, row);
        const nd: Vec3 = [n[0] * depth * h, n[1] * depth * h, n[2] * depth * h]; // protrusion (scaled per-axis)
        const ci = col - px0, ri = row - py0;
        const bx = O[0] + du[0] * ci + dv[0] * ri, by = O[1] + du[1] * ci + dv[1] * ri, bz = O[2] + du[2] * ci + dv[2] * ri;
        // deformed surface corners, then extrude along the box normal
        const s00 = deform([bx, by, bz]);
        const s10 = deform([bx + du[0], by + du[1], bz + du[2]]);
        const s01 = deform([bx + dv[0], by + dv[1], bz + dv[2]]);
        const s11 = deform([bx + du[0] + dv[0], by + du[1] + dv[1], bz + du[2] + dv[2]]);
        const t00 = add3(s00, nd), t10 = add3(s10, nd), t01 = add3(s01, nd), t11 = add3(s11, nd);
        // PER-CORNER skin weights (not one per voxel): the texel's TOP edge is weighted where
        // it sits, its BOTTOM edge where IT sits — exactly like the base mesh's per-row weighting.
        // A rigid whole-voxel weight is why garment/hair voxels SEPARATED at a bending joint
        // (each cube pinned to one bone, adjacent cubes swinging about different pivots). Weighting
        // each corner by its own row edge blends across the warp band, so the shell STRETCHES —
        // and it matches the base body's weights texel-for-texel, so garments deform WITH the skin.
        const wT = def.weightFor(by), wB = def.weightFor(by + dv[1]); // top-edge / bottom-edge weights
        const uv: [number, number] = [(col + 0.5) / TEX, 1 - (row + 0.5) / TEX]; // texel center (nearest)
        vqw(b, t00, t10, t11, t01, n, uv, wT, wT, wB, wB); // outward cap
        // walls where the neighbor is absent (silhouette) or a different height (steps)
        const open = (c: number, r: number) => !solid(c, r) || hAt(c, r) !== h;
        if (col === px0 || open(col - 1, row)) vqw(b, s00, t00, t01, s01, neg3(udir), uv, wT, wT, wB, wB); // -U
        if (col === px1 - 1 || open(col + 1, row)) vqw(b, s10, s11, t11, t10, udir, uv, wT, wB, wB, wT); // +U
        if (row === py0 || open(col, row - 1)) vqw(b, s00, s10, t10, t00, neg3(vdir), uv, wT, wT, wT, wT); // -V (top edge)
        if (row === py1 - 1 || open(col, row + 1)) vqw(b, s01, t01, t11, s11, vdir, uv, wB, wB, wB, wB); // +V (bottom edge)
      }
    }
  }
  emitCornerFills(b, def, faces, solid, hAt);
}

// --- convex-corner fill ------------------------------------------------------
type BSide = "u0" | "u1" | "v0" | "v1";
interface BCell { p0: Vec3; p1: Vec3; c: number; r: number; byTop: number; byBot: number; }
/** One border of a face, cell-by-cell: base-space segment [p0,p1], the atlas texel
 *  (c,r) that owns the cell, and the two row-edge Y's for per-corner skin weights. */
function boundaryOf(f: VFace, side: BSide): BCell[] {
  const [px0, py0, px1, py1] = f.rect;
  const ncols = px1 - px0, nrows = py1 - py0;
  const { O, du, dv } = f;
  const P = (u: number, v: number): Vec3 => [O[0] + du[0] * u + dv[0] * v, O[1] + du[1] * u + dv[1] * v, O[2] + du[2] * u + dv[2] * v];
  const cells: BCell[] = [];
  if (side === "u0" || side === "u1") {
    const u = side === "u0" ? 0 : ncols, col = side === "u0" ? px0 : px1 - 1;
    for (let ri = 0; ri < nrows; ri++) cells.push({ p0: P(u, ri), p1: P(u, ri + 1), c: col, r: py0 + ri, byTop: O[1] + dv[1] * ri, byBot: O[1] + dv[1] * (ri + 1) });
  } else {
    const v = side === "v0" ? 0 : nrows, row = side === "v0" ? py0 : py1 - 1;
    for (let ci = 0; ci < ncols; ci++) cells.push({ p0: P(ci, v), p1: P(ci + 1, v), c: px0 + ci, r: row, byTop: O[1] + dv[1] * v, byBot: O[1] + dv[1] * v });
  }
  return cells;
}
const V_EPS = 1e-4;
function eq3(a: Vec3, b: Vec3): boolean { return Math.abs(a[0] - b[0]) < V_EPS && Math.abs(a[1] - b[1]) < V_EPS && Math.abs(a[2] - b[2]) < V_EPS; }
function cross3(a: Vec3, b: Vec3): Vec3 { return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]; }
function dot3(a: Vec3, b: Vec3): number { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }
function sub3(a: Vec3, b: Vec3): Vec3 { return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]; }
function norm3(a: Vec3): Vec3 { const l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l]; }

/** The garment/overlay voxel shell protrudes each face along its OWN normal, so at a
 *  convex box edge the two faces' caps splay apart and leave the outer corner hollow
 *  ("just covering the surface" — no voxel there). For every one of the 12 box edges,
 *  where BOTH adjacent faces have a solid texel of equal height, bridge their two cap
 *  outer edges with a chamfer quad → the corner reads as a filled, smooth bevel. */
function emitCornerFills(
  b: GeomBuilder, def: VoxelDef, faces: VFace[],
  solid: (c: number, r: number) => boolean, hAt: (c: number, r: number) => number,
): void {
  const { deform, weightFor } = def;
  const bnd: Partial<Record<keyof FaceRects, Record<BSide, BCell[]>>> = {};
  for (const f of faces) bnd[f.key] = { u0: boundaryOf(f, "u0"), u1: boundaryOf(f, "u1"), v0: boundaryOf(f, "v0"), v1: boundaryOf(f, "v1") };
  const nOf: Partial<Record<keyof FaceRects, VFace>> = {};
  for (const f of faces) nOf[f.key] = f;
  const EDGES: [keyof FaceRects, keyof FaceRects][] = [
    ["front", "right"], ["front", "left"], ["back", "right"], ["back", "left"],
    ["top", "front"], ["top", "back"], ["top", "right"], ["top", "left"],
    ["bottom", "front"], ["bottom", "back"], ["bottom", "right"], ["bottom", "left"],
  ];
  const SIDES: BSide[] = ["u0", "u1", "v0", "v1"];
  for (const [ka, kb] of EDGES) {
    const FA = nOf[ka], FB = nOf[kb], ba = bnd[ka], bb = bnd[kb];
    if (!FA || !FB || !ba || !bb) continue;
    // find the shared border (the one whose base segments coincide) and align B to A's order
    let la: BCell[] | null = null, lb: BCell[] | null = null;
    for (const sa of SIDES) {
      for (const sb of SIDES) {
        const ca = ba[sa], cb = bb[sb];
        if (ca.length !== cb.length || !ca.length) continue;
        if (eq3(ca[0].p0, cb[0].p0) && eq3(ca[0].p1, cb[0].p1)) { la = ca; lb = cb; }
        else if (eq3(ca[0].p0, cb[cb.length - 1].p1) && eq3(ca[0].p1, cb[cb.length - 1].p0)) { la = ca; lb = cb.slice().reverse(); }
        if (la) break;
      }
      if (la) break;
    }
    if (!la || !lb) continue;
    for (let i = 0; i < la.length; i++) {
      const a = la[i], bc = lb[i];
      if (!solid(a.c, a.r) || !solid(bc.c, bc.r)) continue;
      const h = hAt(a.c, a.r);
      if (h !== hAt(bc.c, bc.r)) continue;
      const nA: Vec3 = [FA.n[0] * FA.depth * h, FA.n[1] * FA.depth * h, FA.n[2] * FA.depth * h];
      const nB: Vec3 = [FB.n[0] * FB.depth * h, FB.n[1] * FB.depth * h, FB.n[2] * FB.depth * h];
      const d0 = deform(a.p0), d1 = deform(a.p1);
      const A0 = add3(d0, nA), A1 = add3(d1, nA), B0 = add3(d0, nB), B1 = add3(d1, nB);
      const m = norm3([nA[0] + nB[0], nA[1] + nB[1], nA[2] + nB[2]]);
      const wT = weightFor(a.byTop), wB = weightFor(a.byBot);
      const uv: [number, number] = [(a.c + 0.5) / TEX, 1 - (a.r + 0.5) / TEX];
      // pick the winding that faces outward (dot(cross(TR-TL,BL-TL), m) < 0, matching caps)
      let P: [Vec3, Vec3, Vec3, Vec3] = [A0, A1, B1, B0], W: [WeightList, WeightList, WeightList, WeightList] = [wT, wB, wB, wT];
      if (dot3(cross3(sub3(P[1], P[0]), sub3(P[3], P[0])), m) > 0) { P = [A0, B0, B1, A1]; W = [wT, wT, wB, wB]; }
      b.quad(P[0], P[1], P[2], P[3], m, uv, uv, uv, uv, W[0], W[1], W[2], W[3]);
    }
  }
}

function add3(a: Vec3, b: Vec3): Vec3 { return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]; }
function neg3(a: Vec3): Vec3 { return [-a[0], -a[1], -a[2]]; }
/** A flat-shaded voxel quad from 4 explicit corners (winding A,B,C,D outward for
 *  the given normal — matches GeomBuilder's base-face convention), with a per-corner
 *  skin weight so the shell blends across warp bands instead of moving rigidly. */
function vqw(
  b: GeomBuilder, A: Vec3, B: Vec3, C: Vec3, D: Vec3, n: Vec3, uv: [number, number],
  wA: WeightList, wB: WeightList, wC: WeightList, wD: WeightList,
): void {
  b.quad(A, B, C, D, n, uv, uv, uv, uv, wA, wB, wC, wD);
}

// --- rigid box (head) --------------------------------------------------------
const FACES6: ReadonlyArray<{ key: keyof FaceRects; n: THREE.Vector3Tuple; r: THREE.Vector3Tuple; u: THREE.Vector3Tuple }> = [
  { key: "front", n: [0, 0, 1], r: [1, 0, 0], u: [0, 1, 0] },
  { key: "back", n: [0, 0, -1], r: [-1, 0, 0], u: [0, 1, 0] },
  { key: "right", n: [-1, 0, 0], r: [0, 0, 1], u: [0, 1, 0] },
  { key: "left", n: [1, 0, 0], r: [0, 0, -1], u: [0, 1, 0] },
  { key: "top", n: [0, 1, 0], r: [1, 0, 0], u: [0, 0, -1] },
  { key: "bottom", n: [0, -1, 0], r: [1, 0, 0], u: [0, 0, 1] },
];

function allFaces(r: FaceRects): Partial<Record<keyof FaceRects, Rect>> {
  return { front: r.front, back: r.back, right: r.right, left: r.left, top: r.top, bottom: r.bottom };
}

function boxGeometry(wU: number, hU: number, dU: number, rects: Partial<Record<keyof FaceRects, Rect>>, deform: Deform = IDENTITY_DEFORM, sub = 1): THREE.BufferGeometry {
  const half = [wU / 2, hU / 2, dU / 2];
  const pos: number[] = [], nor: number[] = [], uv: number[] = [], idx: number[] = [];
  let v = 0;
  const ext = (a: THREE.Vector3Tuple) => Math.abs(a[0]) * half[0] + Math.abs(a[1]) * half[1] + Math.abs(a[2]) * half[2];
  for (const f of FACES6) {
    const rect = rects[f.key];
    if (!rect) continue;
    const { n, r, u } = f;
    const hr = ext(r), hu = ext(u);
    const c: THREE.Vector3Tuple = [n[0] * half[0], n[1] * half[1], n[2] * half[2]];
    const corner = (su: number, sr: number): Vec3 => deform([
      c[0] + u[0] * hu * su + r[0] * hr * sr, c[1] + u[1] * hu * su + r[1] * hr * sr, c[2] + u[2] * hu * su + r[2] * hr * sr,
    ]);
    const uOf = (sr: number) => (rect[0] + UV_EPS + ((sr + 1) / 2) * (rect[2] - rect[0] - 2 * UV_EPS)) / TEX;
    const vOf = (su: number) => 1 - (rect[1] + UV_EPS + ((1 - su) / 2) * (rect[3] - rect[1] - 2 * UV_EPS)) / TEX;
    // sub×sub grid so `deform` roundness curves the face + edges
    for (let i = 0; i < sub; i++) {
      const su0 = 1 - (2 * i) / sub, su1 = 1 - (2 * (i + 1)) / sub;
      for (let jsub = 0; jsub < sub; jsub++) {
        const sr0 = -1 + (2 * jsub) / sub, sr1 = -1 + (2 * (jsub + 1)) / sub;
        const TL = corner(su0, sr0), TR = corner(su0, sr1), BR = corner(su1, sr1), BL = corner(su1, sr0);
        const qn = quadNormal(TL, TR, BR, BL, n);
        pos.push(...TL, ...TR, ...BR, ...BL);
        for (let k = 0; k < 4; k++) nor.push(qn[0], qn[1], qn[2]);
        uv.push(uOf(sr0), vOf(su0), uOf(sr1), vOf(su0), uOf(sr1), vOf(su1), uOf(sr0), vOf(su1));
        idx.push(v + 0, v + 3, v + 2, v + 0, v + 2, v + 1);
        v += 4;
      }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("normal", new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  return g;
}
